import { useEffect, useMemo, type ReactNode } from 'react'
import { useBlocker } from 'react-router-dom'
import { App } from 'antd'
import { SaveStateProvider } from './SaveStateContext'
import type { SaveStateContextValue } from './SaveStateContextDef'

interface SaveStateFeatureProps {
  /** Children rendered within the provider - can use useSaveStateContext() */
  children: ReactNode
  /** Whether a save operation is currently in progress */
  isSaving: boolean
  /** Timestamp of the last successful save */
  lastSaved: Date | null
  /** Whether there are unsaved changes */
  hasUnsavedChanges: boolean
}

/**
 * Self-contained save state feature.
 *
 * This component wraps children with a provider that exposes save state.
 * The save state is computed in MindMapFlow (hasUnsavedChanges from hash comparison,
 * isSaving and lastSaved from saveQueue) and passed to this feature.
 *
 * Children can use useSaveStateContext() or useOptionalSaveStateContext() hooks
 * to access save state without needing props.
 *
 * @example
 * <SaveStateFeature
 *   isSaving={hasUnsavedChanges || saveQueue.saveState.isSaving}
 *   lastSaved={saveQueue.saveState.lastSaved}
 *   hasUnsavedChanges={hasUnsavedChanges}
 * >
 *   <MindMapHeader /> // Can now use useSaveStateContext()
 * </SaveStateFeature>
 */
export function SaveStateFeature({
  children,
  isSaving,
  lastSaved,
  hasUnsavedChanges,
}: SaveStateFeatureProps) {
  const { modal } = App.useApp()
  const shouldWarn = isSaving || hasUnsavedChanges

  // Block React Router navigation (back button, link clicks) when there are unsaved changes
  const blocker = useBlocker(shouldWarn)

  // Show confirmation dialog when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to leave?',
        okText: 'Leave',
        cancelText: 'Stay',
        onOk: () => blocker.proceed(),
        onCancel: () => blocker.reset(),
      })
    }
  }, [blocker, modal])

  // Warn user when trying to close tab or refresh (beforeunload doesn't work for SPA navigation)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldWarn) {
        e.preventDefault()
        // Modern browsers ignore custom messages, but returnValue is required
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
      }
    }

    if (shouldWarn) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [shouldWarn])

  const value = useMemo<SaveStateContextValue>(
    () => ({
      isSaving,
      lastSaved,
      hasUnsavedChanges,
    }),
    [isSaving, lastSaved, hasUnsavedChanges]
  )

  return <SaveStateProvider value={value}>{children}</SaveStateProvider>
}
